import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { ClassNavigation } from '../components/layout';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { SimpleSurveyDisplay } from '../components/SimpleSurveyDisplay';
import type { CategoryScore, BackgroundResponse } from '../types/survey-student';

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
}


interface StudentSurveyData {
  studentId: string;
  studentName: string;
  scores: CategoryScore[];
  backgroundData: BackgroundResponse;
  hasResults: boolean;
}

interface ClassAggregateData {
  totalStudents: number;
  completedSurveys: number;
  averageScores: CategoryScore[];
  categoryDistribution: { name: string; value: number; color: string }[];
  completionRate: number;
}


const ClassroomInsights = () => {
  const { classId } = useParams();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [aggregateData, setAggregateData] = useState<ClassAggregateData | null>(null);
  const [studentSurveyData, setStudentSurveyData] = useState<StudentSurveyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInsightsData = async () => {
      if (!classId || !user) return;

      setLoading(true);
      try {
        // Load class basic info
        const { data: classData } = await apiService.getClassById(classId);
        
        if (classData) {
          setClassInfo({
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
          });
        }

        // Load all students in the class
        const { data: studentsData } = await apiService.getClassStudents(classId);
        const { data: studentProfilesData } = await apiService.getStudentProfilesByClass(classId);
        
        if (studentsData && studentProfilesData) {
          const studentSurveys: StudentSurveyData[] = [];
          
          // Process each student's survey data
          for (const student of studentsData) {
            if (!student.student?.id) continue;
            
            try {
              // Get survey results for this student
              const { data: surveyResults } = await apiService.getStudentSurveyResults(student.student.id);
              
              // Find student profile data
              const studentProfile = studentProfilesData.find(sp => sp.userId === student.student.id);
              
              if (surveyResults && surveyResults.hasResults && surveyResults.categories?.length > 0) {
                const backgroundData = extractBackgroundFromProfile(studentProfile);
                
                studentSurveys.push({
                  studentId: student.student.id,
                  studentName: student.student.fullName || 'Unknown Student',
                  scores: surveyResults.categories,
                  backgroundData,
                  hasResults: true
                });
              } else {
                studentSurveys.push({
                  studentId: student.student.id,
                  studentName: student.student.fullName || 'Unknown Student',
                  scores: [],
                  backgroundData: {},
                  hasResults: false
                });
              }
            } catch (error) {
              console.warn(`Failed to load survey data for student ${student.student.id}:`, error);
              studentSurveys.push({
                studentId: student.student.id,
                studentName: student.student.fullName || 'Unknown Student',
                scores: [],
                backgroundData: {},
                hasResults: false
              });
            }
          }
          
          setStudentSurveyData(studentSurveys);
          
          // Calculate aggregate data
          const completedSurveys = studentSurveys.filter(s => s.hasResults);
          const totalStudents = studentSurveys.length;
          const completionRate = totalStudents > 0 ? (completedSurveys.length / totalStudents) * 100 : 0;
          
          // Calculate average scores across all categories
          const averageScores = calculateAverageScores(completedSurveys);
          const categoryDistribution = calculateCategoryDistribution(completedSurveys);
          
          setAggregateData({
            totalStudents,
            completedSurveys: completedSurveys.length,
            averageScores,
            categoryDistribution,
            completionRate
          });
        }
      } catch (error) {
        console.error('Failed to load insights data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInsightsData();
  }, [classId, user]);

  const extractBackgroundFromProfile = (studentProfile: any): BackgroundResponse => {
    if (!studentProfile?.backgroundData && !studentProfile?.responses) {
      return {};
    }

    const data = studentProfile.backgroundData || studentProfile.responses || {};
    
    return {
      name: data.name || studentProfile.fullName || '',
      email: data.email || studentProfile.email || '',
      classLevel: data.classLevel || '',
      hometown: data.hometown || '',
      classCount: data.classCount || '',
      majorCount: data.majorCount || '',
      major: data.major || data.majors || '',
      firstLanguage: data.firstLanguage || '',
      dailyLanguage: data.dailyLanguage || '',
      englishProficiency: data.englishProficiency || 0,
      speakingComfort: data.speakingComfort || 0,
      listeningSkill: data.listeningSkill || 0,
      readingComfort: data.readingComfort || 0,
      writingAbility: data.writingAbility || 0,
      memorizing: data.courseworkEmphasis?.memorizing || data.memorizing || '',
      applying: data.courseworkEmphasis?.applying || data.applying || '',
      analyzing: data.courseworkEmphasis?.analyzing || data.analyzing || '',
      evaluating: data.courseworkEmphasis?.evaluating || data.evaluating || '',
      forming: data.courseworkEmphasis?.forming || data.forming || '',
      numericalAnalysis: data.numericalTasks?.numericalAnalysis || data.numericalAnalysis || '',
      numericalProblems: data.numericalTasks?.numericalProblems || data.numericalProblems || '',
      evaluatingNumerical: data.numericalTasks?.evaluatingNumerical || data.evaluatingNumerical || '',
      papers5Pages: data.writingTasks?.papers5Pages || data.papers5Pages || '',
      papers6to10Pages: data.writingTasks?.papers6to10Pages || data.papers6to10Pages || '',
      papers11Plus: data.writingTasks?.papers11Plus || data.papers11Plus || '',
      previousCourses: data.previousCourses || '',
      reasonsForTaking: data.reasonsForTaking || [],
      preferredCommunication: data.preferredCommunication || ''
    };
  };

  const calculateAverageScores = (completedSurveys: StudentSurveyData[]): CategoryScore[] => {
    if (completedSurveys.length === 0) return [];
    
    const categoryMap = new Map<string, { totalScore: number; count: number; maxPossible: number; description: string; items: number[] }>();
    
    completedSurveys.forEach(survey => {
      survey.scores.forEach(score => {
        if (!categoryMap.has(score.name)) {
          categoryMap.set(score.name, {
            totalScore: 0,
            count: 0,
            maxPossible: score.maxPossible,
            description: score.description,
            items: score.items || []
          });
        }
        const existing = categoryMap.get(score.name)!;
        existing.totalScore += score.score;
        existing.count += 1;
      });
    });
    
    return Array.from(categoryMap.entries()).map(([name, data]) => ({
      name,
      score: data.totalScore / data.count,
      maxPossible: data.maxPossible,
      description: data.description,
      items: data.items
    }));
  };

  const calculateCategoryDistribution = (completedSurveys: StudentSurveyData[]): { name: string; value: number; color: string }[] => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    
    if (completedSurveys.length === 0) {
      return [
        { name: 'No Data', value: 100, color: '#9ca3af' }
      ];
    }
    
    const scoreRanges = {
      'High (5-7)': 0,
      'Medium (3-5)': 0,
      'Low (1-3)': 0
    };
    
    let totalScores = 0;
    
    completedSurveys.forEach(survey => {
      survey.scores.forEach(score => {
        totalScores++;
        if (score.score > 5) scoreRanges['High (5-7)']++;
        else if (score.score > 3) scoreRanges['Medium (3-5)']++;
        else scoreRanges['Low (1-3)']++;
      });
    });
    
    return Object.entries(scoreRanges).map(([range, count], index) => ({
      name: range,
      value: totalScores > 0 ? Math.round((count / totalScores) * 100) : 0,
      color: colors[index] || '#9ca3af'
    }));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900">Class not found</h2>
            <p className="text-gray-600 mt-2">Unable to load class insights.</p>
          </div>
        </div>
      </div>
    );
  }


  if (!aggregateData || !classInfo) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Survey Data Available</h2>
            <p className="text-gray-600">
              No student survey data found for this class. Students need to complete their surveys first.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ClassNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            📊 {classInfo.name} - Class Survey Insights
          </h1>
          <p className="text-gray-600">{classInfo.code} • {classInfo.term}</p>
        </div>

        <div className="space-y-8">
          {/* Class Overview Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{aggregateData.totalStudents}</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Completed Surveys</p>
                <p className="text-2xl font-bold text-emerald-600">{aggregateData.completedSurveys}</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                <p className="text-2xl font-bold text-blue-600">{aggregateData.completionRate.toFixed(1)}%</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Categories Analyzed</p>
                <p className="text-2xl font-bold text-purple-600">{aggregateData.averageScores.length}</p>
              </div>
            </div>
          </div>

          {/* Score Distribution Chart */}
          {aggregateData.categoryDistribution.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Distribution Across All Students</h3>
              <div className="flex justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={aggregateData.categoryDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({name, value}) => `${name}: ${value}%`}
                    >
                      {aggregateData.categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Class Average Learning Profile */}
          {aggregateData.averageScores.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Class Average Learning Profile</h3>
                <p className="text-gray-600">
                  Aggregated analysis of {aggregateData.completedSurveys} completed student surveys
                </p>
              </div>
              
              <SimpleSurveyDisplay
                scores={aggregateData.averageScores}
                backgroundData={{}} // No individual background data for class average
              />
            </div>
          )}


          {/* Students Without Results */}
          {studentSurveyData.filter(s => !s.hasResults).length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                Students Without Survey Results ({studentSurveyData.filter(s => !s.hasResults).length})
              </h3>
              <p className="text-yellow-800 mb-4">
                The following students have not yet completed their learning profile surveys:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {studentSurveyData
                  .filter(student => !student.hasResults)
                  .map((student) => (
                    <div key={student.studentId} className="text-sm text-yellow-700">
                      • {student.studentName}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassroomInsights;